import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Attachments, FilteredOrders, ModalResult, ModalResultMicrobiology, ModalResultMultiline, Notas, Order, OrdersGlobal, SelectedOrder, StudiesAddType, SysIResultados } from '../../types/types.orders';
import { Paciente } from '../../types/types.patient';
import { contextPatient } from '../../types/types.system';

const currentOrder: SelectedOrder = {
  Mode: 'view',
  ModePatient: 'view',
  OrderData: { AditionalFields: {} },
  PatientData: { Pc_Identificacion: '' },
  IdOrder: -1,
  ModalPatient: false,
  LoadingPatient: false,
  CreatePatientAndUpdate: true,
  ContextPatient: 'none',
  NoFindedDocument: '',
  StudiesData: [],
  ModalStudies: false,
  LoadingStudies: false,
  IdTransaction: null,
  Attachments: [],
  Notes: [],
};

// Define the initial state using that type
const initialState: OrdersGlobal = {
  Loading: false,
  PanelVisible: true,
  PanelPined: true,
  CurrentOrder: currentOrder,
  FilteredOrders: [],
  FiltersToOrders: null,
  FiltersToOrdersText: [],
  // Filtros persistentes para resultados
  ResultsFilters: {
    selectedStates: [],
  },
  // Tab actualmente visible en infoOrder
  currentActiveTab: 1, // Default: Studies (tab 1) - Primera pestaña al abrir órdenes
};

//Encargado de manejar todas las órdenes del sistema y configuración general

export const ordersSlice = createSlice({
  name: 'OrderGlobal',
  initialState,
  reducers: {
    // Acción para migrar estados existentes
    migrateState: (state) => {
      if (!state.ResultsFilters) {
        state.ResultsFilters = { selectedStates: [] };
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.Loading = action.payload;
    },
    setMode: (state, action: PayloadAction<{ mode: 'new' | 'update' | 'view' | 'loading'; idTran?: string; idOrd?: number }>) => {
      state.CurrentOrder.Mode = action.payload.mode;
      if (action.payload.mode === 'new') {
        state.CurrentOrder = { ...currentOrder, Mode: action.payload.mode };
      }
      if (action.payload.idTran) {
        state.CurrentOrder.IdTransaction = action.payload.idTran;
      }
      if (action.payload.mode === 'view' && action.payload.idOrd === -1) {
        state.CurrentOrder = currentOrder;
      }
    },
    setPanelPined: (state, action: PayloadAction<boolean>) => {
      state.PanelPined = action.payload;
    },
    setPanelVisible: (state, action: PayloadAction<boolean>) => {
      state.PanelVisible = action.payload;
    },
    setPatient: (state, action: PayloadAction<any>) => {
      if (state.CurrentOrder) {
        state.CurrentOrder.PatientData = action.payload;
      }
    },
    setPropPatient: (state, action: PayloadAction<{ NomParam: string; Value: any }>) => {
      if (state.CurrentOrder) {
        state.CurrentOrder.PatientData = {
          ...state.CurrentOrder.PatientData,
          [action.payload.NomParam]: action.payload.Value,
        };
      }
    },
    setOrderData: (state, action: PayloadAction<any>) => {
      if (state.CurrentOrder) {
        state.CurrentOrder.OrderData = action.payload;
      }
    },
    setOrdEstRpmData: (state, action: PayloadAction<{ NomParam: string; Value: any; IdEst: number }>) => {
      //Buscamos primero el estudio
      const { NomParam, Value, IdEst } = action.payload;
      const studyIndex = state.CurrentOrder.StudiesData.findIndex((study) => study.Es_Id === IdEst);
      const study = state.CurrentOrder.StudiesData[studyIndex];
      if (!study.AditionalFields) {
        study.AditionalFields = {};
      }
      study.AditionalFields[NomParam] = Value;
    },
    clearOrder: (state) => {
      state.CurrentOrder = currentOrder;
    },
    clearSelectedOrders: (state) => {
      state.FilteredOrders = [];
    },
    clearPatient: (state) => {
      state.CurrentOrder.PatientData = { Pc_Identificacion: '' };
    },
    setModePatient: (state, action: PayloadAction<'new' | 'update' | 'view'>) => {
      state.CurrentOrder.ModePatient = action.payload;
    },
    setModalPatient: (state, action: PayloadAction<{ visible: boolean; context: contextPatient }>) => {
      state.CurrentOrder.ContextPatient = action.payload.context;
      if (state.CurrentOrder.ModalPatient) {
        state.CurrentOrder.ModePatient = 'view';
      }
      state.CurrentOrder.ModalPatient = action.payload.visible;

      state.CurrentOrder.CreatePatientAndUpdate = action.payload.visible;
    },
    setCurrentPatientToTransfer: (state, action: PayloadAction<Paciente | undefined>) => {
      state.CurrentPatientToTransfer = action.payload;
    },
    setModalEstudies: (state) => {
      state.CurrentOrder.ModalStudies = !state.CurrentOrder.ModalStudies;
    },
    setLoadingPatient: (state) => {
      state.CurrentOrder.LoadingPatient = !state.CurrentOrder.LoadingPatient;
    },
    updateFieldOrder: (state, action: PayloadAction<{ field: string; castMode: 'UPPER' | 'LOWER' | null; value: any }>) => {
      const { field, castMode, value } = action.payload;
      let resp = value;
      switch (castMode) {
        case 'LOWER':
          resp = value.toString().toLocaleLowerCase();
          break;
        case 'UPPER':
          resp = value.toString().toUpperCase();
          break;
      }
      if (field.includes('AditionalFields')) {
        state.CurrentOrder.OrderData.AditionalFields[field.split('.')[1]] = resp;
        return;
      }
      state.CurrentOrder.OrderData[field] = resp;
    },
    setOrderFromServer: (state, action: PayloadAction<{ IdTran: string; OrderData: any; StudiesData: any; IdOrder: number }>) => {
      if (state.CurrentOrder.IdTransaction === action.payload.IdTran || !action.payload.IdTran) {
        state.CurrentOrder.OrderData = action.payload.OrderData;
        state.CurrentOrder.IdOrder = action.payload.IdOrder;
        state.CurrentOrder.StudiesData = action.payload.StudiesData;
        state.CurrentOrder.Mode = 'view';
      }
    },
    addStudy: (state, action: PayloadAction<StudiesAddType>) => {
      state.CurrentOrder.StudiesData.push(action.payload);
    },
    removeStudy: (state, action: PayloadAction<number>) => {
      state.CurrentOrder.StudiesData = state.CurrentOrder.StudiesData.filter((x) => x.Es_Id !== action.payload);
    },
    updateStateAndRem: (state, action: PayloadAction<{ type: 'State' | 'Rem'; Es_Id: number; state: boolean | undefined }>) => {
      let actual;
      actual = state.CurrentOrder.StudiesData.find((x) => x.Es_Id === action.payload.Es_Id);

      if (actual && actual.Accion !== 1) {
        actual.Editado = true;
      }

      switch (action.payload.type) {
        case 'State':
          if (actual) {
            if (action.payload.state !== undefined) {
              actual.Estado = action.payload.state;
              break;
            }
            actual.Estado = !actual.Estado;
          }
          break;
        case 'Rem':
          if (actual) {
            if (action.payload.state !== undefined) {
              actual.Remitido = action.payload.state;
              // Si se quita la remisión, limpiar el laboratorio de referencia
              if (!action.payload.state) {
                actual.Es_Lr_Id = null;
              }
              break;
            }
            // Toggle: si está remitido, quitarlo y limpiar Es_Lr_Id
            if (actual.Remitido) {
              actual.Remitido = false;
              actual.Es_Lr_Id = null;
            } else {
              actual.Remitido = true;
            }
          }
          break;
        default:
          break;
      }
    },
    updateLabRef: (state, action: PayloadAction<{ Es_Id: number; Es_Lr_Id: number|null }>) => {
      const study = state.CurrentOrder.StudiesData.find((x) => x.Es_Id === action.payload.Es_Id);
      
      if (study) {
        study.Es_Lr_Id = action.payload.Es_Lr_Id;
        // Marcar como remitido cuando se asigna un laboratorio de referencia
        if (action.payload.Es_Lr_Id !== null) {
          study.Remitido = true;
        }
        
        if (study.Accion !== 1) {
          study.Editado = true;
        }
      }
    },
    setNotesFromServer: (state, action: PayloadAction<Notas[]>) => {
      state.CurrentOrder.Notes = action.payload;
    },
    setAttachmentFromServer: (state, action: PayloadAction<Attachments[]>) => {
      state.CurrentOrder.Attachments = action.payload;
    },
    removeAttachment: (state, action: PayloadAction<number>) => {
      if (!state.CurrentOrder.Attachments) {
        return;
      }
      state.CurrentOrder.Attachments = state.CurrentOrder.Attachments.filter((x) => x.Oad_Id !== action.payload);
    },
    setNote: (state, action: PayloadAction<Notas>) => {
      if (!state.CurrentOrder.Notes) {
        state.CurrentOrder.Notes = [action.payload];
        return;
      }
      state.CurrentOrder.Notes?.push(action.payload);
    },
    setResultFromServer: (state, action: PayloadAction<SysIResultados[]>) => {
      state.CurrentOrder.Results = action.payload;
    },
    updateResult: (state, action: PayloadAction<{ Res_Id: number; Res_Result: string }>) => {
      const index = state.CurrentOrder.Results?.findIndex((x) => x.Res_Id === action.payload.Res_Id);
      if (index && index !== -1 && state.CurrentOrder.Results) {
        state.CurrentOrder.Results[index].Res_Resultado = action.payload.Res_Result;
      }
    },
    showModalTemplates: (state, action: PayloadAction<ModalResult>) => {
      state.CurrentOrder.ModalResultTemplate = action.payload;
    },
    showModalImages: (state, action: PayloadAction<ModalResult>) => {
      state.CurrentOrder.ModalResultImage = action.payload;
    },
    showModalMicrobiology: (state, action: PayloadAction<ModalResultMicrobiology>) => {
      state.CurrentOrder.ModalResultMicrobiology = action.payload;
    },
    showModalMultiline: (state, action: PayloadAction<ModalResultMultiline>) => {
      state.CurrentOrder.ModalResultMultiline = action.payload;
    },
    showModalImagenesDx: (state, action: PayloadAction<ModalResult>) => {
      state.CurrentOrder.ModalResultImagenesDx = action.payload;
    },
    showModalNotes: (state, action: PayloadAction<ModalResult>) => {
      state.CurrentOrder.ModalNoteVal = action.payload;
    },
    showModalHistory: (state, action: PayloadAction<ModalResult>) => {
      state.CurrentOrder.ModalHistory = action.payload;
    },
    setFilteredOrders: (state, action: PayloadAction<Order[]>) => {
      state.FilteredOrders = action.payload;
    },
    setFilteresToOrders: (state, action: PayloadAction<FilteredOrders>) => {
      try {
        if (!state.CurrentOrder) {
          return;
        }
        state.FiltersToOrders = action.payload;
      } catch (error) {
        console.error('Error al establecer los filtros:', error);
      }
    },
    setFiltersToOrdersText: (state, action: PayloadAction<string[]>) => {
      state.FiltersToOrdersText = action.payload;
    },
    clearFilteredOrders: (state) => {
      state.FilteredOrders = [];
      state.FiltersToOrders = null;
      state.FiltersToOrdersText = [];
    },
    setFindedDocument: (state, action: PayloadAction<string>) => {
      state.CurrentOrder.NoFindedDocument = action.payload;
    },
    setResultsSelectedStates: (state, action: PayloadAction<number[]>) => {
      // Asegurar que ResultsFilters existe
      if (!state.ResultsFilters) {
        state.ResultsFilters = { selectedStates: [] };
      }
      state.ResultsFilters.selectedStates = action.payload;
    },
    clearResultsFilters: (state) => {
      // Asegurar que ResultsFilters existe
      if (!state.ResultsFilters) {
        state.ResultsFilters = { selectedStates: [] };
      }
      state.ResultsFilters.selectedStates = [];
    },
    // Acción para actualizar la tab activa en InfoOrder
    setCurrentActiveTab: (state, action: PayloadAction<number>) => {
      state.currentActiveTab = action.payload;
    },
  },
});

export const {
  migrateState,
  setCurrentPatientToTransfer,
  showModalNotes,
  showModalHistory,
  showModalTemplates,
  showModalImages,
  showModalMicrobiology,
  showModalMultiline,
  showModalImagenesDx,
  setPanelPined,
  setLoading,
  setPanelVisible,
  setOrderData,
  setMode,
  clearOrder,
  clearSelectedOrders,
  setPatient,
  setPropPatient,
  clearPatient,
  setModePatient,
  setModalPatient,
  setLoadingPatient,
  updateFieldOrder,
  addStudy,
  updateStateAndRem,
  updateLabRef,
  setModalEstudies,
  setOrdEstRpmData,
  setOrderFromServer,
  setNotesFromServer,
  setAttachmentFromServer,
  removeAttachment,
  setNote,
  removeStudy,
  setResultFromServer,
  updateResult,
  setFilteredOrders,
  setFilteresToOrders,
  setFiltersToOrdersText,
  clearFilteredOrders,
  setFindedDocument,
  setResultsSelectedStates,
  clearResultsFilters,
  setCurrentActiveTab,
} = ordersSlice.actions;
